import { COUNTRIES, LOCALE_NAMES } from "@repo/shared";
import type { ClientViewFilter, DatePeriod, EditFieldsResponse } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { useMemo, useState, useTransition } from "react";
import { type ActivityDateFieldValue } from "@/components/clients/activity-date-fields";
import { buildFilterRegistry, resolveFilterField } from "@/components/clients/filters";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { del } from "@/lib/api/_client";
import { useClientCostCategories } from "@/lib/hooks/use-client-cost-categories";
import { useClientViews, useEditFields } from "@/lib/hooks/use-clients";
import { useTeams } from "@/lib/hooks/use-teams";
import { useUserLanguage } from "@/lib/i18n";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { ClientViewDialog } from "./client-view-dialog";
import { ACTIVITY_DATE_LABELS, T } from "./client-views-wrapper.i18n";
import type { ClientView } from "./types";

type ClientViewsWrapperProps = {
  tenantUsers: Array<{ id: string; name: string }>;
  supportedLanguages: string[];
};

export function ClientViewsWrapper({ tenantUsers, supportedLanguages }: ClientViewsWrapperProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const { views: initialViews } = useClientViews();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingView, setEditingView] = useState<ClientView | null>(null);
  const [isPending, startTransition] = useTransition();
  const { confirm, ConfirmationDialog } = useConfirm();

  const { editFields } = useEditFields();
  const { clientCostCategories } = useClientCostCategories();
  const { teams } = useTeams();
  const registry = useMemo(
    () =>
      buildFilterRegistry({
        userLanguage,
        localize,
        customFields: editFields as EditFieldsResponse,
        tenantUsers,
        supportedLanguages,
        costCategories: clientCostCategories.filter((c) => c.isActive),
        teams,
        countries: COUNTRIES,
        localeNames: LOCALE_NAMES,
      }),
    [
      userLanguage,
      localize,
      editFields,
      tenantUsers,
      supportedLanguages,
      clientCostCategories,
      teams,
    ],
  );

  const invalidateViews = () => queryClient.invalidateQueries({ queryKey: ["clientViews"] });

  const handleCreate = () => {
    setEditingView(null);
    setDialogOpen(true);
  };

  const handleEdit = (view: ClientView) => {
    setEditingView(view);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingView(null);
  };

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirm({
      title: t.deleteView,
      description: t.deleteViewConfirmation(name),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    startTransition(async () => {
      try {
        await del(`/internal/client-views/${id}`);
      } catch (err: any) {
        await confirm({
          title: t.error,
          description: err.message || t.failedToDeleteView,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
      }
      invalidateViews();
    });
  };

  const tDates = useT(ACTIVITY_DATE_LABELS);
  const getActivityDateLabel = (fieldValue: string): string =>
    tDates[fieldValue as ActivityDateFieldValue] ?? fieldValue;

  const periodLabel = (period: DatePeriod): string =>
    (
      ({
        today: t.today,
        this_week: t.thisWeek,
        this_month: t.thisMonth,
        this_quarter: t.thisQuarter,
        this_year: t.thisYear,
        older_than_month: t.olderThanMonth,
        older_than_quarter: t.olderThanQuarter,
        older_than_half_year: t.olderThanHalfYear,
        older_than_year: t.olderThanYear,
      }) satisfies Record<DatePeriod, string>
    )[period];

  /** A short value description for a registry-resolved filter's badge — not the field label, just what it's set to. */
  const genericValueSummary = (filter: ClientViewFilter): string => {
    if (filter.type === "owner") {
      const hasMe = filter.values.includes("__ME__");
      const otherCount = filter.values.filter((v) => v !== "__ME__").length;
      return hasMe
        ? otherCount > 0
          ? t.ownerMePlus(String(otherCount))
          : t.ownerMe
        : t.ownerUsers(String(otherCount));
    }
    if (filter.type === "favourite") return t.ownerMe;
    if (filter.type === "clientType") {
      const typeLabels: Record<string, string> = {
        Person: t.typePerson,
        Company: t.typeCompany,
        Group: t.typeGroup,
      };
      return filter.values.map((v) => typeLabels[v] || v).join(", ");
    }
    if (
      filter.type === "country" ||
      filter.type === "language" ||
      filter.type === "costCategory" ||
      filter.type === "team"
    ) {
      return t.selected(String(filter.values.length));
    }
    if (filter.type === "standardEmpty")
      return filter.operator === "empty" ? t.isEmpty : t.isNotEmpty;
    // QL-512 — the field label already reads "… over"; the badge only adds the amount.
    // (Age is not here: its badge shows the registry's draft, see `getFilterSummary`.)
    if (filter.type === "cash") return String(filter.amount);
    if (filter.type === "datePeriod") return periodLabel(filter.period);
    if (filter.type === "customField") {
      if (filter.operator === "empty" || filter.operator === "notEmpty") {
        return filter.operator === "empty" ? t.isEmpty : t.isNotEmpty;
      }
      if (filter.operator === "boolean") return filter.value ? t.yes : t.no;
      if ("value" in filter) return String(filter.value);
      if ("values" in filter) return t.selected(String(filter.values.length));
    }
    return "";
  };

  const getFilterSummary = (filters: ClientViewFilter[]) => {
    if (filters.length === 0) return t.noFilters;

    return filters.map((filter, index) => {
      if (filter.type === "activityDate") {
        const fieldLabel = getActivityDateLabel(filter.dateField);
        const operatorLabel = filter.operator === "in_last" ? t.inLast : t.notInLast;
        const monthsLabel =
          filter.months === 1 ? t.monthSingular : t.monthsPlural(String(filter.months));
        return (
          <Badge key={`activityDate-${filter.dateField}`} variant="secondary" className="mr-1">
            {fieldLabel}: {operatorLabel} {monthsLabel}
          </Badge>
        );
      }
      const resolved = resolveFilterField(filter, registry);
      if (!resolved) {
        // Still applied by the list (a deactivated field, or a numeric operator the
        // editor no longer offers) — say so, or a filtered view reads as unfiltered.
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: filters have no stable id of their own
          <Badge key={`unavailable-${index}`} variant="outline" className="mr-1">
            {t.unavailableFilter}
          </Badge>
        );
      }
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: filters have no stable id of their own
        <Badge key={`${resolved.field.key}-${index}`} variant="secondary" className="mr-1">
          {resolved.field.label}:{" "}
          {/* Age reads the draft: a legacy "gt 50" is shown as what it means, "Age over: 51". */}
          {filter.type === "age" ? resolved.draft.value : genericValueSummary(filter)}
        </Badge>
      );
    });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-4">
        <p className="text-sm text-muted-foreground">{t.clientViewsDescription}</p>
        <Button onClick={handleCreate}>{t.addView}</Button>
      </div>

      {initialViews.length === 0 ? (
        <EmptyState>
          {t.noViewsYet}
          <span className="block text-sm mt-2">{t.createViewHint}</span>
        </EmptyState>
      ) : (
        <Table variant="data">
          <TableHeader>
            <TableRow>
              <TableHead>{t.name}</TableHead>
              <TableHead>{t.filters}</TableHead>
              <TableHead>{t.default}</TableHead>
              <TableHead className="text-right">{t.actions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {initialViews.map((view) => (
              <TableRow key={view.id}>
                <TableCell className="font-medium">
                  <div>
                    {localize(view.nameI18n)}
                    {view.isStandard && (
                      <span className="ml-2 text-xs text-muted-foreground">{t.standard}</span>
                    )}
                    {view.descriptionI18n && (
                      <p className="text-sm text-muted-foreground">
                        {localize(view.descriptionI18n)}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">{getFilterSummary(view.filters)}</div>
                </TableCell>
                <TableCell>
                  {view.isDefault && <Badge variant="default">{t.default}</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {view.isStandard ? (
                    <span className="text-xs text-muted-foreground">{t.cannotEdit}</span>
                  ) : (
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(view)}
                        disabled={isPending}
                        tooltip={t.edit}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(view.id, localize(view.nameI18n))}
                        disabled={isPending}
                        tooltip={t.delete}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ClientViewDialog
        open={dialogOpen}
        view={editingView}
        tenantUsers={tenantUsers}
        supportedLanguages={supportedLanguages}
        onClose={handleDialogClose}
      />
      <ConfirmationDialog />
    </>
  );
}
